import { Tabs } from "expo-router";
import { Platform, ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { useFeatureFlags, useBooleanFlag, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { useAuth, useTenant, useTenantReady, AccountNotConfiguredView } from "@src/features/tenants";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useRefetchOnForeground } from "@src/hooks/useRefetchOnForeground";
import { Auth0UpsellModal } from "@src/components/Auth0UpsellModal";
import { SiteWideDrawerProvider } from "@src/components/AppDrawer/SiteWideDrawerProvider.native";
import { useIsTenantAdmin } from "@visioni/a3s-access/app";

export default function TabsLayout() {
  const { theme } = useUnistyles();
  const { getBooleanFlag } = useFeatureFlags();
  const { isAuthenticated } = useAuth();
  const { selectedTenant, tenants, refreshTenants, error: tenantError } = useTenant();
  const { isReady, isEmpty, isLoading: isLoadingTenants } = useTenantReady();

  // Refetch data when app comes to foreground
  useRefetchOnForeground();

  const isAlarmsTabEnabled = getBooleanFlag(FEATURE_FLAGS.ALARMS_TAB);
  const isActivityTabEnabled = getBooleanFlag(FEATURE_FLAGS.ACTIVITY_TAB);
  const isSiteWideControlsEnabled = useBooleanFlag(FEATURE_FLAGS.SITE_WIDE_CONTROLS);

  const { isTenantAdmin } = useIsTenantAdmin();
  const canViewActivity = isActivityTabEnabled && isTenantAdmin;

  // Don't render tabs content during logout to prevent useTenantId errors
  // Check both isAuthenticated AND selectedTenant because during logout,
  // tenant is cleared before isAuthenticated is set to false
  const hasTenant = isAuthenticated && !!selectedTenant;

  // Show "Account not configured" when:
  // - User is authenticated but has no tenant
  // - Not currently loading tenants
  // - Either there's an error OR tenants is empty (meaning we fetched but got none)
  const showNoTenantView = isAuthenticated && !hasTenant && isReady && (!!tenantError || isEmpty);

  // Show loading only while actively fetching tenants
  if (isAuthenticated && !hasTenant && !showNoTenantView && isLoadingTenants) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" bg="background" gap="lg">
        <ActivityIndicator size="large" color={theme.colors.textMuted} />
        <Text textStyle="md/regular" color="textMuted">
          Loading organisations...
        </Text>
      </View>
    );
  }

  if (showNoTenantView) {
    return <AccountNotConfiguredView error={tenantError?.message} onRetry={() => void refreshTenants()} />;
  }

  return (
    <SiteWideDrawerProvider>
      <Auth0UpsellModal />
      <Tabs
        initialRouteName="overview"
        screenOptions={{
          headerShown: true,
          headerStyle: {
            backgroundColor: theme.colors.background,
          },
          headerTintColor: theme.colors.text,
          headerBackButtonDisplayMode: "minimal",
          tabBarStyle: {
            backgroundColor: theme.colors.surface,
            borderTopColor: theme.colors.border,
            ...Platform.select({
              ios: {
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0,
                shadowRadius: 0,
              },
              android: {
                elevation: 8,
              },
            }),
          },
          tabBarActiveTintColor: theme.colors.interactive.controls.selected_midground,
          tabBarInactiveTintColor: theme.colors.textMuted,
          tabBarHideOnKeyboard: Platform.OS === "android",
        }}
      >
        <Tabs.Screen
          name="overview"
          options={{
            title: "All cameras",
            tabBarLabel: "Cameras",
            href: "/overview",
            tabBarIcon: ({ color, size, focused }) => (
              <Icon name={focused ? "video" : "videoOutline"} size={size} color={color} />
            ),
            tabBarAccessibilityLabel: "Overview tab",
            tabBarButtonTestID: "tab-overview",
            headerShadowVisible: false,
          }}
        />
        <Tabs.Screen
          name="traffic-flow"
          options={{
            title: "Crossline Mapping",
            tabBarLabel: "Crossline Mapping",
            href: null,
            tabBarIcon: ({ color, size }) => <Icon name="personMoving" size={size} color={color} />,
            tabBarAccessibilityLabel: "Crossline Mapping tab",
            tabBarButtonTestID: "tab-traffic-flow",
          }}
        />
        <Tabs.Screen
          name="activity"
          options={{
            title: "Activity",
            tabBarLabel: "Activity",
            href: canViewActivity ? "/activity" : null,
            tabBarIcon: ({ color, size }) => <Icon name="motion" size={size} color={color} />,
            tabBarAccessibilityLabel: "Activity tab",
            tabBarButtonTestID: "tab-activity",
          }}
        />
        <Tabs.Screen
          name="sites"
          options={{
            title: "Controls",
            tabBarLabel: "Controls",
            href: isSiteWideControlsEnabled ? "/sites" : null,
            tabBarIcon: ({ color, size, focused }) => (
              <Icon name={focused ? "settingsCamera" : "settingsCameraOutline"} size={size} color={color} />
            ),
            tabBarAccessibilityLabel: "Controls tab",
            tabBarButtonTestID: "tab-sites",
            headerShown: false,
          }}
        />
        <Tabs.Screen
          name="alarms"
          options={{
            title: "Alarms",
            tabBarLabel: "Alarms",
            href: !isSiteWideControlsEnabled && isAlarmsTabEnabled ? "/alarms" : null,
            tabBarIcon: ({ color, size, focused }) => (
              <Icon name={focused ? "settingsCamera" : "settingsCameraOutline"} size={size} color={color} />
            ),
            tabBarAccessibilityLabel: "Alarms tab",
            tabBarButtonTestID: "tab-alarms",
          }}
        />
        <Tabs.Screen
          name="vehicles"
          options={{
            title: "Vehicle Search",
            tabBarLabel: "Vehicle Search",
            href: null,
            tabBarIcon: ({ color, size }) => <Icon name="truck" size={size} color={color} />,
            tabBarAccessibilityLabel: "Vehicle Search tab",
            tabBarButtonTestID: "tab-vehicles",
          }}
        />
        <Tabs.Screen
          name="settings"
          options={{
            title: "Settings",
            tabBarLabel: "Settings",
            href: "/settings",
            tabBarIcon: ({ color, size, focused }) => (
              <Icon name={focused ? "settings" : "settingsOutline"} size={size} color={color} />
            ),
            tabBarAccessibilityLabel: "Settings tab",
            tabBarButtonTestID: "tab-settings",
          }}
        />
      </Tabs>
    </SiteWideDrawerProvider>
  );
}
